import { IEventoDeAcesso, MotivoSemEnvio } from "./gestao-de-usuarios.types";

// why: F3 (plano 11 T7). Textos da tela Gestão de usuários. As recusas vêm do backend com a
// frase do §10 da spec; aqui ficam só os textos que a própria tela escreve.

export const NOME_DO_PAPEL: Record<string, string> = {
  CONSULTA: "Consulta",
  OPERACAO: "Operação",
  CONFIGURACAO: "Configuração",
  GESTAO: "Gestão",
};

// "CORRETORA_GESTAO" -> "Gestão"; o lado vem da organização, não do nome do papel.
export const papelLegivel = (codigo: string) => NOME_DO_PAPEL[codigo.split("_").slice(1).join("_")] ?? codigo;

const quem = (e: IEventoDeAcesso) => e.autor ?? (e.canal === "MIGRACAO" ? "A migração de acessos" : "A Conducco");
const papel = (e: IEventoDeAcesso) => papelLegivel(e.papel ?? "");

export const FRASE_DO_EVENTO: Record<string, (e: IEventoDeAcesso) => string> = {
  VINCULAR: (e) => `${quem(e)} deu acesso a ${e.alvo}.`,
  DESVINCULAR: (e) => `${quem(e)} removeu o acesso de ${e.alvo}.`,
  ADICIONAR_PAPEL: (e) => `${quem(e)} deu ${papel(e)} a ${e.alvo}.`,
  REMOVER_PAPEL: (e) => `${quem(e)} tirou ${papel(e)} de ${e.alvo}.`,
  TROCAR_ALCANCE: (e) =>
    e.alcanceNovo === "SELECAO"
      ? `${quem(e)} passou ${e.alvo} a empresas marcadas.`
      : e.alcanceNovo === "ORGANIZACAO"
        ? `${quem(e)} passou ${e.alvo} a toda a carteira.`
        : `${quem(e)} mudou o alcance de ${e.alvo}.`,
  ADICIONAR_SELECAO: (e) =>
    e.empresa
      ? `${quem(e)} incluiu ${e.empresa} no alcance de ${e.alvo}.`
      : `${quem(e)} deu a ${e.alvo} acesso a uma empresa.`,
  RECUSAR_SELECAO: (e) =>
    e.empresa
      ? `${quem(e)} deixou ${e.empresa} fora do alcance de ${e.alvo}.`
      : `${quem(e)} decidiu não dar a ${e.alvo} acesso a uma empresa.`,
  REMOVER_SELECAO: (e) =>
    e.empresa
      ? `${quem(e)} tirou ${e.empresa} do alcance de ${e.alvo}.`
      : `${quem(e)} tirou de ${e.alvo} o acesso a uma empresa.`,
  ADICIONAR_RESTRICAO: (e) => `${quem(e)} restringiu uma atividade para ${e.alvo}.`,
  REMOVER_RESTRICAO: (e) => `${quem(e)} tirou uma restrição de atividade de ${e.alvo}.`,
  BLOQUEAR: (e) => `${quem(e)} suspendeu o acesso de ${e.alvo}.`,
  DESBLOQUEAR: (e) => `${quem(e)} reativou o acesso de ${e.alvo}.`,
  MUDAR_MODO: (e) => `${quem(e)} mudou o modo de verificação de acessos desta organização.`,
  MUDAR_PARAMETRO: (e) => `${quem(e)} mudou uma regra de acesso da plataforma.`,
  MARCAR_REVISADA: (e) => `${quem(e)} confirmou quem administra os acessos.`,
  CRIAR_USUARIO: (e) => `${quem(e)} criou a conta de ${e.alvo}.`,
  APAGAR_USUARIO: (e) => `${quem(e)} apagou a conta de ${e.alvo}.`,
  CONCEDER_ADMIN: (e) => `${quem(e)} deu acesso de administrador da plataforma a ${e.alvo}.`,
  REVOGAR_ADMIN: (e) => `${quem(e)} tirou o acesso de administrador da plataforma de ${e.alvo}.`,
  BAIXAR_DADO_PESSOAL: (e) => `${quem(e)} baixou um arquivo com dados pessoais.`,
  CONVIDAR: (e) => `${quem(e)} convidou ${e.alvo}.`,
  REENVIAR_CONVITE: (e) => `${quem(e)} reenviou o convite de ${e.alvo}.`,
  MUDAR_PAPEIS_DO_CONVITE: (e) => `${quem(e)} mudou os papéis do convite de ${e.alvo}.`,
  CANCELAR_CONVITE: (e) => `${quem(e)} cancelou o convite de ${e.alvo}.`,
  MUDAR_ALCANCE_DO_CONVITE: (e) => `${quem(e)} mudou o alcance do convite de ${e.alvo}.`,
  MUDAR_RECEBIMENTO_BALANCO: (e) => {
    const de = e.autor === e.alvo ? "o próprio Balanço do dia" : `o Balanço do dia de ${e.alvo}`;
    const verbo = e.recebeBalanco === null ? "mudou" : e.recebeBalanco ? "ligou" : "desligou";
    return `${quem(e)} ${verbo} ${de}.`;
  },
  // why: não é ação do backend; nasce de juntarConfirmacoes (a revisão tira e dá de novo a Gestão).
  CONFIRMAR_ADMINISTRADOR: (e) => `${quem(e)} confirmou ${e.alvo} como administrador dos acessos.`,
};

export function fraseDoEvento(e: IEventoDeAcesso): string {
  const base = FRASE_DO_EVENTO[e.acao]?.(e) ?? `${quem(e)} mudou um acesso.`;
  return e.assistida ? `${base.slice(0, -1)} (pela corretora).` : base;
}

// why: a revisão confirma quem fica com Gestão tirando-a de todos e dando-a de novo aos
// escolhidos, no mesmo instante (plano 11 §7c). Mostrar "tirou" e "deu" lado a lado confunde.
export function juntarConfirmacoes(eventos: IEventoDeAcesso[]): IEventoDeAcesso[] {
  // why: pela pessoa (alvoId), não pelo nome; dois homônimos não se confundem.
  const chave = (e: IEventoDeAcesso) => `${e.criadoEm}|${e.alvoId ?? e.alvo}|${e.papel}`;
  const dados = new Set(eventos.filter((e) => e.acao === "ADICIONAR_PAPEL").map(chave));
  const tirados = new Set(eventos.filter((e) => e.acao === "REMOVER_PAPEL").map(chave));
  return eventos
    .filter((e) => !(e.acao === "REMOVER_PAPEL" && dados.has(chave(e))))
    .map((e) =>
      e.acao === "ADICIONAR_PAPEL" && tirados.has(chave(e)) && (e.papel ?? "").endsWith("_GESTAO")
        ? { ...e, acao: "CONFIRMAR_ADMINISTRADOR" }
        : e,
    );
}

export const TEXTOS = {
  TITULO: "Gestão de usuários",
  ERRO_AO_CARREGAR: "Não foi possível carregar os acessos. Tente de novo.",
  ERRO_AO_SALVAR: "Não foi possível salvar. Tente de novo.",
  PARTE_SALVA: (motivo: string) => `${motivo} O que veio antes foi salvo; confira a lista.`,
  SEM_ACESSO: (organizacao: string) =>
    `Seu acesso não inclui esta ação em Gestão de usuários. Fale com o administrador da ${organizacao}.`,
  NAO_REVISADA:
    "A corretora ainda não confirmou quem administra os acessos desta organização. Por enquanto a tela é só de consulta.",
  GESTAO_PROPRIA: (empresa: string) => `A ${empresa} já tem administrador próprio. Peça a ele esta mudança.`,
  CONSULTA_INCLUIDA: "Todo acesso inclui consulta.",
  CONVITE_ENVIADO: "Convite enviado. A pessoa aparece em Convites pendentes até aceitar.",
  CONVITES_PENDENTES: "Convites pendentes",
  CONVITES_EXPLICA: "Ainda não aceitaram. Os papéis valem a partir do aceite e podem ser mudados antes dele.",
  ENVIADO_EM: (data: string) => `Enviado em ${data.slice(0, 10).split("-").reverse().join("/")}`,
  CONVITE_VENCIDO: "Convite vencido",
  CONVITE_VENCIDO_REENVIE: "Convite vencido: reenvie",
  JA_TEM_CONTA: "Já tem conta",
  JA_TEM_CONTA_CANCELE: "Já tem conta: cancele e use Convidar",
  ERRO_AO_CARREGAR_CONVITES: "Não foi possível carregar os convites pendentes. Tente de novo.",
  ACESSO_DADO_COM_AVISO: "Acesso dado. A pessoa já tinha conta e recebeu um aviso por e-mail.",
  ACESSO_DADO_SEM_AVISO: "Acesso dado. A pessoa já tinha conta, mas o aviso por e-mail não saiu.",
  REVISAO_EXPLICA: "Quem você marcar fica com Gestão. Os outros perdem a Gestão e mantêm os demais papéis.",
  REVISAO_EMPRESA: "Se ninguém da empresa ficar com Gestão, a corretora continua a administrar por ela.",
  TODA_A_CARTEIRA: "Toda a carteira",
  ESTA_ORGANIZACAO: "Esta organização",
  EQUIPE_DA_CORRETORA: "Equipe da corretora",
  SUSPENSO: "Suspenso",
  REVISADA: "Administradores confirmados",
  NAO_REVISADA_CURTO: "Administradores a confirmar",
  ADMINISTRADOR_PROPRIO: "Administrador próprio",
  CORRETORA_ADMINISTRA: "A corretora administra",
  N_EMPRESAS: (n: number) => (n === 1 ? "1 empresa" : `${n} empresas`),
  EMPRESAS_MARCADAS: "Empresas marcadas",
  PENDENTE: "Pendente",
  PENDENCIAS: "Decisões de alcance pendentes",
  PENDENCIAS_EXPLICA:
    "Empresas novas na carteira, ainda sem decisão. Enquanto não decidir, a pessoa não vê a empresa nem a recebe no Balanço do dia.",
  RAIZ_INTEIRA: "Já tem as outras empresas desta raiz",
  CONFIRMA_SELECAO: (n: number, m: number, nome: string) =>
    `${n} de ${m} empresas da carteira. As outras ficam fora do alcance de ${nome}.`,
  SELECAO_VAZIA: "Marque ao menos uma empresa.",
  BALANCO_DO_DIA: "Balanço do dia",
  BALANCO_DE: (nome: string) => `Balanço do dia de ${nome}`,
  LIGADO_SEM_ENVIO: (motivo: string) => `ligado, mas não recebe: ${motivo}`,
  MOTIVO: { SUSPENSO: "suspenso", SEM_EMAIL: "sem e-mail", RESTRICAO: "com restrição" } as Record<MotivoSemEnvio, string>,
  BALANCO_LIGADO: (nome: string) => `Balanço do dia de ${nome} ligado.`,
  BALANCO_DESLIGADO: (nome: string) => `Balanço do dia de ${nome} desligado.`,
  MEU_BALANCO_LIGADO: "Você voltou a receber o Balanço do dia.",
  MEU_BALANCO_DESLIGADO: "Você não recebe mais o Balanço do dia.",
  ALCANCE: "Alcance",
  ALCANCE_DO_CONVITE: (nome: string) => `Alcance do convite de ${nome} atualizado. Vale quando a pessoa aceitar.`,
  MUDAR: "Mudar",
  SEM_EVENTOS: "Nenhuma mudança de acesso registrada ainda.",
};
